/* tslint:disable */
/* eslint-disable */
/**
 * Tumelo API
 * This is Tumelo\'s API. It drives increased shareholder engagement by providing transparency of the organizations owned by investors and giving them a voice by allowing them to vote on important issues at these organizations.  The main uses of this API are:  * Transparency - To provide a breakdown to an investor of the organizations in which they are invested. * Voting - To provide the ability for an investor to vote on a poll concerning organizations in which they are invested.  ## Status  This API is currently available as a closed **BETA**. We invite interested parties to [give us feedback](mailto:support@tumelo.com) on this API, its documentation, or anything else about Tumelo\'s transparency service prior to the API becoming generally available.  ## Changelog  A changelog for the api can be found [here](CHANGELOG.md).  ## Access The API is available at the following URL: ``` https://api.prod.tumelo.com/v1 ```  For information on how to obtain access to the API, please [contact us](mailto:support@tumelo.com).  ## Development vs Production Habitats Development and Production environments are separated into their own habitats. Please [contact us](mailto:support@tumelo.com) when you\'re ready to go into production and require access to your company\'s live habitat.  # Using the API  ## Case Sensitivity URLs, headers, path parameters, query parameter names, and query parameter values are all case sensitive. Be careful to use the correct case when constructing requests.  ## Error Handling Requests may return an error indicating that there was a problem responding. The response will contain an HTTP status code as well as a JSON object in the following format which describes the error in more detail: ```json {   \"message\": \"Resource not found\" } ```  ## Pagination Requests that return lists and describe pagination parameters support paginated results. Results are separated into multiple pages, where the size of each page is determined by the `pageSize` query parameter. If omitted, `pageSize` defaults to 50 records. When a response includes a `nextPageToken` in the response body, this token value should be passed as a query parameter `nextPageToken` to the same endpoint in order to retrieve the next page of results. This process should be repeated until the response doesn’t include a `nextPageToken`, indicating that the end of the results has been reached.  When passing the `nextPageToken` parameter, all other filter and query parameters present in the previous request, **must** be filled. Filter parameters *must not* be changed while paginating since this will result in an error or an incorrect overall list. The `nextPageToken` will not be present in the response body if the end of the list has been reached and no further results are available.  ## String Formats The following string formats are used throughout this API.  ### date-time The valid format is date-time notation as defined by [RFC 3339, section 5.6](https://tools.ietf.org/html/rfc3339#section-5.6).  For example: `2020-11-30T10:45:28Z`  # Support If you have any technical questions relating to the Tumelo API, please [contact us](mailto:support@tumelo.com), explaining your question or issue in as much detail as possible, including the operation you were performing and the ISIN or Organization ID you were querying, as well as the results you expected and the actual results or error message you observed. You can expect a response within one business day.  ## Data Issues During development and testing in your development habitat we make available a test data set that includes some sample data constructed to illustrate functionality. It is not intended to be used for any purpose other than testing and/or demonstrating functionality, and should not be relied upon to be an accurate reflection of the underlying holdings of any real fund.  If you have any questions or concerns about the data accuracy or completeness of the data in the production environment, please [report the matter](mailto:support@tumelo.com) to us, giving as much information as possible about the issue.  # Compositions and Breakdowns  An instrument may have a composition, this means it is a fund invested in other instruments.  A breakdown is the bottom level components of a composite instrument or account. By this, we mean organizations and cash that a particular composite instrument or account is invested into either directly or through funds.  Breakdowns also have an `other` listing, this is for funds that only have partial information available, or where an instrument is not mapped to an organization. 
 *
 * The version of the OpenAPI document: 0.145.1
 * Contact: support@tumelo.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

import { exists, mapValues } from '../runtime';
/**
 * A meeting of shareholders of an organization. Typically an AGM or EGM.
 * @export
 * @interface GeneralMeeting
 */
export interface GeneralMeeting {
    /**
     * The general meeting identifier assigned by Tumelo.
     * @type {string}
     * @memberof GeneralMeeting
     */
    id: string;
    /**
     * The id of the organization this general meeting corresponds to.
     * @type {string}
     * @memberof GeneralMeeting
     */
    organizationId: string;
    /**
     * The type of general meeting.
     * @type {string}
     * @memberof GeneralMeeting
     */
    generalMeetingType: GeneralMeetingGeneralMeetingTypeEnum;
    /**
     * The date of the general meeting.
     * @type {Date}
     * @memberof GeneralMeeting
     */
    date: Date;
    /**
     * A URL to a document or website giving further details about the general meeting. Deprecated - use proxy_notice_url, announcement_url or results_url instead.
     * @type {string}
     * @memberof GeneralMeeting
     * @deprecated
     */
    url?: string;
    /**
     * The time the general meeting was announced.
     * @type {Date}
     * @memberof GeneralMeeting
     */
    announcementTime?: Date;
    /**
     * A URL that links to a document or website containing the official general meeting announcement.
     * @type {string}
     * @memberof GeneralMeeting
     * @deprecated
     */
    announcementUrl?: string;
    /**
     * The URL of the official full proxy statement for the meeting, giving information about the meeting agenda, the voting rules and all explanatory notes. 
     * 
     * For example, in the UK this is generally referred to as the “Meeting notice” and is usually published on the issuer’s investor relations website and is also filed with the FCA’s National Storage Mechanism (NSM) with a document category of “Notice of AGM”. In some cases filings in NSM will include both a brief announcement of a meeting, but without the full meeting information ([Diageo example](https://data.fca.org.uk/artefacts/NSM/RNS/4489382.html)), and a second filing also categorised as a “Notice of AGM”, which contains the full meeting information ([Diageo example](https://data.fca.org.uk/artefacts/NSM/RNS/4489382.html)). It is the link for the latter full documentation that is required here. 
     * 
     * As a further example, in the US these documents are usually referred to as the “Definitive Proxy Statement” or a [SEC Form DEF 14A](https://www.investopedia.com/terms/s/sec-form-def-14a.asp). These are often published on the issuer’s investor relations website and on [SEC’s EDGAR](https://www.sec.gov/edgar/searchedgar/companysearch) archive. As an example of the document we require, see [Microsoft’s Proxy Statement](https://www.sec.gov/Archives/edgar/data/789019/000119312522270484/d318171ddef14a.htm) from 2022. Note that additional proxy materials are sometimes also published on EDGAR, including the proxy form. These are not currently required ([Microsoft example](https://www.sec.gov/Archives/edgar/data/789019/000119312522270484/d318171ddef14a.htm) from 2022 of the link that is not required)
     * @type {string}
     * @memberof GeneralMeeting
     */
    proxyNoticeUrl?: string;
    /**
     * The URL of the official results posting, wherever possible including the number of votes for, against etc. 
     * 
     * As an example, in the UK these documents are usually filed on the issuer’s investor relations website, and also on the National Storage Mechanism (NSM). In NSM, they have a category of “Result of AGM”. More than one document is sometimes categorised in this way. Wherever it’s available, we require the document that includes all the vote counts, but failing that the one which states the outcome of each proposal is acceptable. For example, see the [AGM results](https://data.fca.org.uk/artefacts/NSM/RNS/4429402.html) for Marks & Spencer in 2022. 
     * 
     * As a further example, in the US AGM results are generally published in an [SEC Form 8-K](https://www.investopedia.com/terms/1/8-k.asp). There are typically multiple Form 8-K’s filed by an issuer throughout the year relating to a variety of events of interest to shareholder. The 8-K filed immediately after the AGM date typically includes the AGM vote results required. For example, see the [AGM results for Microsoft](https://www.sec.gov/ix?doc=/Archives/edgar/data/0000789019/000119312521343711/d205343d8k.htm) from 2021.
     * @type {string}
     * @memberof GeneralMeeting
     */
    resultsUrl?: string;
    /**
     * The record date for the general meeting.
     * @type {Date}
     * @memberof GeneralMeeting
     */
    recordDate?: Date;
}


/**
 * @export
 */
export const GeneralMeetingGeneralMeetingTypeEnum = {
    Annual: 'annual',
    Extraordinary: 'extraordinary',
    Special: 'special',
    General: 'general',
    CourtOrdered: 'court_ordered',
    PostalBallot: 'postal_ballot',
    BondHolder: 'bond_holder'
} as const;
export type GeneralMeetingGeneralMeetingTypeEnum = typeof GeneralMeetingGeneralMeetingTypeEnum[keyof typeof GeneralMeetingGeneralMeetingTypeEnum];


/**
 * Check if a given object implements the GeneralMeeting interface.
 */
export function instanceOfGeneralMeeting(value: object): boolean {
    let isInstance = true;
    isInstance = isInstance && "id" in value;
    isInstance = isInstance && "organizationId" in value;
    isInstance = isInstance && "generalMeetingType" in value;
    isInstance = isInstance && "date" in value;

    return isInstance;
}

export function GeneralMeetingFromJSON(json: any): GeneralMeeting {
    return GeneralMeetingFromJSONTyped(json, false);
}

export function GeneralMeetingFromJSONTyped(json: any, ignoreDiscriminator: boolean): GeneralMeeting {
    if ((json === undefined) || (json === null)) {
        return json;
    }
    return {
        
        'id': json['id'],
        'organizationId': json['organizationId'],
        'generalMeetingType': json['generalMeetingType'],
        'date': (new Date(json['date'])),
        'url': !exists(json, 'url') ? undefined : json['url'],
        'announcementTime': !exists(json, 'announcementTime') ? undefined : (new Date(json['announcementTime'])),
        'announcementUrl': !exists(json, 'announcementUrl') ? undefined : json['announcementUrl'],
        'proxyNoticeUrl': !exists(json, 'proxyNoticeUrl') ? undefined : json['proxyNoticeUrl'],
        'resultsUrl': !exists(json, 'resultsUrl') ? undefined : json['resultsUrl'],
        'recordDate': !exists(json, 'recordDate') ? undefined : (new Date(json['recordDate'])),
    };
}

export function GeneralMeetingToJSON(value?: GeneralMeeting | null): any {
    if (value === undefined) {
        return undefined;
    }
    if (value === null) {
        return null;
    }
    return {
        
        'id': value.id,
        'organizationId': value.organizationId,
        'generalMeetingType': value.generalMeetingType,
        'date': (value.date.toISOString().substr(0,10)),
        'url': value.url,
        'announcementTime': value.announcementTime === undefined ? undefined : (value.announcementTime.toISOString().substr(0,10)),
        'announcementUrl': value.announcementUrl,
        'proxyNoticeUrl': value.proxyNoticeUrl,
        'resultsUrl': value.resultsUrl,
        'recordDate': value.recordDate === undefined ? undefined : (value.recordDate.toISOString().substr(0,10)),
    };
}

